var __importDefault = (this && this.__importDefault) || function (mod) {
  return (mod && mod.__esModule) ? mod : { "default": mod };
};
/**
 * DeleteConfirmationModal — Right-side sliding panel for confirming event deletion.
 * Matches the same size and placement as EventDetailModal / AddEditEventModal.
 * Content mirrors the original GRM calendar delete-confirmation-popup.tpl.html:
 *   • Title: "DELETE NON RECURRING EVENT" or "DELETE RECURRING EVENT"
 *   • Confirmation text + comments display
 *   • For recurring events: radio buttons for "Delete this Occurrence" / "Delete this Series"
 *   • OK / CANCEL buttons
 */
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { createFocusTrap } from '../../utils/accessibilityUtils';
/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */
const s = {
    backdrop: {
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.4)',
        zIndex: 10001,
    },
    panel: {
        position: 'fixed',
        top: 0, right: 0, bottom: 0,
        width: '450px',
        maxWidth: '100vw',
        backgroundColor: '#fff',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.18)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 10002,
        animation: 'slideInFromRight 0.25s ease-out',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        borderBottom: '1px solid #edebe9',
        backgroundColor: '#faf9f8',
    },
    headerTitle: {
        fontSize: '18px',
        fontWeight: 600,
        color: '#0078d4',
        margin: 0,
        textTransform: 'uppercase',
    },
    closeBtn: {
        background: 'none',
        border: 'none',
        fontSize: '22px',
        color: '#605e5c',
        cursor: 'pointer',
        padding: '2px 6px',
        lineHeight: 1,
        borderRadius: '4px',
    },
    body: {
        flex: 1,
        overflowY: 'auto',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    confirmText: {
        fontSize: '14px',
        color: '#323130',
        margin: 0,
        lineHeight: '1.5',
    },
    commentsText: {
        fontSize: '14px',
        color: '#323130',
        margin: 0,
    },
    radioGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        marginTop: '4px',
    },
    radioLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        color: '#323130',
        cursor: 'pointer',
    },
    radioInput: {
        margin: 0,
        accentColor: '#0078d4',
        cursor: 'pointer',
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px',
        padding: '12px 20px',
        borderTop: '1px solid #edebe9',
        backgroundColor: '#faf9f8',
    },
    okBtn: {
        padding: '8px 24px',
        borderRadius: '4px',
        fontSize: '14px',
        fontWeight: 600,
        cursor: 'pointer',
        border: '1px solid #0078d4',
        backgroundColor: '#0078d4',
        color: '#fff',
    },
    cancelBtn: {
        padding: '8px 24px',
        borderRadius: '4px',
        fontSize: '14px',
        fontWeight: 600,
        cursor: 'pointer',
        border: '1px solid #8a8886',
        backgroundColor: '#fff',
        color: '#323130',
    },
};
/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
const getComments = (event) => {
    var _a, _b, _c, _d;
    const cp = event.customProperties;
    if (!cp)
        return '';
    const comments = (_d = (_b = (_a = cp.comments) !== null && _a !== void 0 ? _a : cp.Comments) !== null && _b !== void 0 ? _b : (_c = cp.AdditionalProperties) === null || _c === void 0 ? void 0 : _c.Comments) !== null && _d !== void 0 ? _d : '';
    return typeof comments === 'string' ? comments : Array.isArray(comments) ? comments.join(' ') : String(comments || '');
};
const getIsRecurrent = (event) => {
    var _a, _b, _c;
    const cp = event.customProperties;
    if (!cp)
        return false;
    // Check direct IsRecurrent / isRecurrent flags
    const val = (_a = cp.IsRecurrent) !== null && _a !== void 0 ? _a : cp.isRecurrent;
    if (typeof val === 'boolean' && val)
        return true;
    if (typeof val === 'string' && val.toLowerCase() === 'true')
        return true;
    // Fallback: check RecurrencePattern — if it's set and not 'None', the event is recurring
    const pattern = (_b = cp.RecurrencePattern) !== null && _b !== void 0 ? _b : cp.recurrencePattern;
    if (pattern && typeof pattern === 'string' && pattern.toLowerCase() !== 'none' && pattern !== '')
        return true;
    // Fallback: check AdditionalProperties (some API responses nest recurrence info there)
    const ap = cp.AdditionalProperties;
    if (ap) {
        const apRecurrent = (_c = ap.IsRecurrent) !== null && _c !== void 0 ? _c : ap.isRecurrent;
        if (typeof apRecurrent === 'boolean' && apRecurrent)
            return true;
        if (typeof apRecurrent === 'string' && apRecurrent.toLowerCase() === 'true')
            return true;
    }
    return false;
};
/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export const DeleteConfirmationModal = memo(({ isOpen, event, onConfirm, onCancel, }) => {
    const ref = useRef(null);
    const [occurrence, setOccurrence] = useState('single');
    // Reset occurrence radio when a new event opens
    useEffect(() => {
        if (isOpen) {
            setOccurrence('single');
        }
    }, [isOpen, event]);
    // Focus trap & escape key
    useEffect(() => {
        if (!isOpen)
            return;
        const handler = (e) => {
            if (e.key === 'Escape')
                onCancel();
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [isOpen, onCancel]);
    // Auto-focus OK button
    useEffect(() => {
        if (isOpen && ref.current) {
            const btn = ref.current.querySelector('#btnOK_DeleteEvent');
            btn === null || btn === void 0 ? void 0 : btn.focus();
            return createFocusTrap(ref.current);
        }
    }, [isOpen]);
    const handleConfirm = useCallback(() => {
        if (!event)
            return;
        const isRecurrent = getIsRecurrent(event);
        // For recurring: "single" → don't delete series, "complete" → delete series
        // For non-recurring: always delete (ShouldDeleteSeries = true matches prod behavior)
        const shouldDeleteSeries = isRecurrent ? occurrence === 'complete' : true;
        onConfirm(event, shouldDeleteSeries);
    }, [event, occurrence, onConfirm]);
    if (!isOpen || !event)
        return null;
    const isRecurrent = getIsRecurrent(event);
    const comments = getComments(event);
    const title = isRecurrent ? 'Delete Recurring Event' : 'Delete Non Recurring Event';
    const confirmText = isRecurrent
        ? 'Do you want to delete all occurrences of the event?'
        : 'Are you sure you want to delete this event?';
    return (React.createElement("div", { style: s.backdrop, onClick: (e) => { if (e.target === e.currentTarget)
            onCancel(); } },
        React.createElement("style", null, `@keyframes slideInFromRight { from { transform: translateX(100%); } to { transform: translateX(0); } }`),
        React.createElement("div", { ref: ref, style: s.panel, role: "alertdialog", "aria-label": title, "aria-modal": "true" },
            React.createElement("div", { style: s.header },
                React.createElement("h2", { style: s.headerTitle }, title),
                React.createElement("button", { style: s.closeBtn, onClick: onCancel, "aria-label": "Close delete confirmation" }, "\u00D7")),
            React.createElement("div", { style: s.body },
                React.createElement("p", { style: s.confirmText }, confirmText),
                comments && (React.createElement("p", { style: s.commentsText },
                    "Comments : ",
                    comments)),
                isRecurrent && (React.createElement("div", { style: s.radioGroup, role: "radiogroup", "aria-label": "Delete scope" },
                    React.createElement("label", { style: s.radioLabel },
                        React.createElement("input", { type: "radio", name: "deleteOccurrenceType", value: "single", checked: occurrence === 'single', onChange: () => setOccurrence('single'), style: s.radioInput, "aria-label": "Delete this occurrence" }),
                        "Delete this Occurrence"),
                    React.createElement("label", { style: s.radioLabel },
                        React.createElement("input", { type: "radio", name: "deleteOccurrenceType", value: "complete", checked: occurrence === 'complete', onChange: () => setOccurrence('complete'), style: s.radioInput, "aria-label": "Delete this series" }),
                        "Delete this Series")))),
            React.createElement("div", { style: s.footer },
                React.createElement("button", { id: "btnOK_DeleteEvent", type: "button", style: s.okBtn, onClick: handleConfirm, "aria-label": "Confirm delete event" }, "OK"),
                React.createElement("button", { id: "btnCancel_DeleteEvent", type: "button", style: s.cancelBtn, onClick: onCancel, "aria-label": "Cancel deletion" }, "CANCEL")))));
});
DeleteConfirmationModal.displayName = 'DeleteConfirmationModal';
export default DeleteConfirmationModal;
//# sourceMappingURL=DeleteConfirmationModal.js.map